/**
 * Utility functions for handling compressed content from the backend
 */

/**
 * Decompress content using browser's built-in decompression APIs
 * @param {string} compressedContent - The compressed content string (base64 encoded deflate)
 * @returns {Promise<string>} - The decompressed content
 */
export const decompressContent = async (compressedContent) => {
  if (!compressedContent) {
    return '';
  }

  try {
    // Check if this looks like base64 encoded data (contains only base64 characters)
    const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;

    if (!base64Regex.test(compressedContent)) {
      // Not base64 encoded, return as is (regular content)
      return compressedContent;
    }

    // Convert base64 to Uint8Array
    const compressedData = Uint8Array.from(atob(compressedContent), (c) =>
      c.charCodeAt(0),
    );

    // Decompress using deflate (backend uses Zlib::Deflate.deflate)
    const decompressedData = await decompressDeflate(compressedData);
    const decompressedContent = new TextDecoder().decode(decompressedData);

    return decompressedContent;
  } catch (error) {
    console.error('Error decompressing content:', error);
    // Return original content if decompression fails
    return compressedContent;
  }
};

/**
 * Decompress deflate data using browser's built-in APIs
 * @param {Uint8Array} compressedData - The compressed data
 * @returns {Promise<Uint8Array>} - The decompressed data
 */
const decompressDeflate = async (compressedData) => {
  // Use the Compression Streams API if available (modern browsers)
  if ('CompressionStream' in window) {
    const stream = new Response(compressedData).body;
    const decompressedStream = stream.pipeThrough(
      new DecompressionStream('deflate'),
    );
    const response = new Response(decompressedStream);
    return new Uint8Array(await response.arrayBuffer());
  } else {
    // Fallback: try to use pako or other libraries if available
    // For now, return the original data if CompressionStream is not available
    throw new Error('CompressionStream not supported in this browser');
  }
};

/**
 * Process document content by checking for compressed_content first, then falling back to content
 * @param {Object} document - The document object from API response
 * @returns {Promise<string>} - The processed content
 */
export const processDocumentContent = async (document) => {
  if (!document) {
    return '';
  }

  // Check if compressed_content is available
  if (document.compressed_content) {
    try {
      const decompressedContent = await decompressContent(
        document.compressed_content,
      );

      return decompressedContent;
    } catch (error) {
      console.error(
        'Failed to decompress content, falling back to regular content:',
        error,
      );
    }
  }

  // Fallback to regular content field

  return document.content || '';
};
